import { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Check, Copy, Share2 } from "lucide-react";
import { createPrivateChallenge } from "utils/supabase";
import { linkChallengeInviteCode } from "utils/challengeLink";
import BackdropAlert from "components/Custom/BackdropAlert.js";

export default function RetarOtrosButton({ examId, examTitle = "Reto ENARM", buttonSx = {} }) {
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [copied, setCopied] = useState(false);
  const [backdropAlert, setBackdropAlert] = useState({
    open: false,
    severity: "info",
    title: "",
    message: "",
    primaryAction: null,
  });

  const closeBackdropAlert = () => {
    setBackdropAlert((prev) => ({ ...prev, open: false }));
  };

  const showBackdropAlert = ({ severity = "info", title = "", message = "" }) => {
    setBackdropAlert({
      open: true,
      severity,
      title,
      message,
      primaryAction: { label: "Cerrar", onClick: closeBackdropAlert },
    });
  };

  const inviteUrl = challenge?.invite_code
    ? `${window.location.origin}/admin/reto/${challenge.invite_code}`
    : "";

  const handleCreateChallenge = async () => {
    if (!examId || loading) return;

    setLoading(true);
    try {
      const { data, error } = await createPrivateChallenge(examId, examTitle);
      if (error || !data?.success || !data?.invite_code) {
        throw new Error(error?.message || "No se pudo crear el reto.");
      }
      linkChallengeInviteCode(examId, data.invite_code);
      setChallenge(data);
      setCopied(false);
    } catch (error) {
      console.error("Error creating private challenge:", error);
      showBackdropAlert({
        severity: "error",
        title: "No se pudo crear el reto",
        message: error?.message || "Ocurrio un error inesperado.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setChallenge(null);
  };

  const handleCopyLink = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Error copying invite link:", error);
    }
  };

  const handleShare = async () => {
    if (!inviteUrl) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: challenge?.title || examTitle,
          text: `Te reto a superar mi puntaje en "${challenge?.title || examTitle}" en ENARM Apex.`,
          url: inviteUrl,
        });
      } catch (error) {
        // El usuario puede cancelar el share sheet, no es un error real.
        if (error?.name !== "AbortError") {
          console.error("Error sharing invite link:", error);
        }
      }
    } else {
      handleCopyLink();
    }
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleCreateChallenge}
        disabled={loading || !examId}
        sx={{
          textTransform: "none",
          fontWeight: 700,
          borderRadius: "10px",
          py: 1.1,
          px: 2.5,
          bgcolor: "#7c3aed",
          boxShadow: "none",
          "&:hover": {
            bgcolor: "#6d28d9",
            boxShadow: "none",
          },
          ...buttonSx,
        }}
      >
        {loading ? "Creando reto..." : "⚔️ Reta a otros"}
      </Button>

      <Dialog open={Boolean(challenge)} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>⚔️ Reta a otros jugadores</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: "#4b5563", fontSize: 14 }}>
            Comparte este enlace para que otros jugadores respondan el mismo examen y comparen su puntaje contigo.
          </Typography>

          <TextField
            fullWidth
            value={inviteUrl}
            slotProps={{
              htmlInput: { readOnly: true },
              input: {
                endAdornment: (
                  <InputAdornment position="end" sx={{ ml: 1 }}>
                    <Tooltip title={copied ? "Copiado" : "Copiar URL"}>
                      <IconButton
                        onClick={handleCopyLink}
                        aria-label={copied ? "URL copiada" : "Copiar URL"}
                        sx={{
                          width: 36,
                          height: 36,
                          color: copied ? "#16a34a" : "#7c3aed",
                          backgroundColor: copied ? "#ecfdf5" : "#f5f3ff",
                          "&:hover": { backgroundColor: copied ? "#dcfce7" : "#ede9fe" },
                        }}
                      >
                        {copied ? <Check size={18} /> : <Copy size={18} />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }}
          />

          {challenge?.question_count ? (
            <Typography sx={{ mt: 1.5, color: "#6b7280", fontSize: 13 }}>
              {challenge.question_count} preguntas · {challenge.duration_minutes} min
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDialog}>Cerrar</Button>
          <Button
            variant="contained"
            startIcon={<Share2 size={18} />}
            onClick={handleShare}
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#7c3aed", "&:hover": { bgcolor: "#6d28d9" } }}
          >
            Compartir
          </Button>
        </DialogActions>
      </Dialog>

      <BackdropAlert
        open={backdropAlert.open}
        onClose={closeBackdropAlert}
        severity={backdropAlert.severity}
        title={backdropAlert.title}
        message={backdropAlert.message}
        primaryAction={backdropAlert.primaryAction}
      />
    </>
  );
}
